import React, { useState, useEffect, useMemo } from 'react';
import { SearchDropdown } from '../../../../../components';
import { Attachment, Rail, PanelOrientation } from '../../../../../types';
import { PANEL_ROWS_MAX, PANEL_ORIENTATION_OPTIONS, SystemKey, SYSTEM_LABELS } from '../../../../../constants';
import { mmToInch } from '../../../../../utils';
import { SystemModuleInfo } from '../../../../../store/selectors/createCRSelectors';
import { AuroraPanelPoolRow, StructureRoofDetail } from '../../../../../utils/aurora-mapper.utils';

interface PanelRow {
  id: string;
  orientation: PanelOrientation;
  panel_count: number;
  system_key?: SystemKey;
}

interface AttachmentRailSelectorProps {
  selectedAttachment?: Attachment;
  selectedRail?: Rail;
  onAttachmentChange: (attachment: Attachment | undefined) => void;
  onRailChange: (rail: Rail | undefined) => void;
  disabled?: boolean;
  isEnabled?: boolean;
  selectedAttachment2?: Attachment;
  selectedRail2?: Rail;
  onAttachment2Change?: (attachment: Attachment | undefined) => void;
  onRail2Change?: (rail: Rail | undefined) => void;
  showSecondAttachmentRail?: boolean;
  onToggleSecondAttachmentRail?: () => void;
  attachments: Attachment[];
  isLoading: boolean;
  error?: string;
  panelRows1?: PanelRow[];
  onPanelRows1Change?: (rows: PanelRow[]) => void;
  panelRows2?: PanelRow[];
  onPanelRows2Change?: (rows: PanelRow[]) => void;
  totalModuleQuantity?: number;
  globalTotalAssigned?: number;
  globalRemainingPanels?: number;
  configuredSystems?: SystemKey[];
  allSystemsModuleData?: Record<SystemKey, SystemModuleInfo>;
  perSystemAssigned?: Partial<Record<SystemKey, number>>;
  // Aurora pool props (Case 1)
  auroraPanelRowsPool?: AuroraPanelPoolRow[] | null;
  structureNumber?: number;
  structuresRoofsDetails?: StructureRoofDetail[] | null;
  onAssignPoolRow?: (rowId: string) => void;
  onUnassignPoolRow?: (rowId: string) => void;
  onAssignPoolRow2?: (rowId: string) => void;
  onUnassignPoolRow2?: (rowId: string) => void;
}

const DEFAULT_SYSTEM_KEY: SystemKey = 'system_one';

const generateRowId = () => `row_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

// Color palette per system for visual distinction
const SYSTEM_COLORS: Record<SystemKey, { bg: string; border: string; dot: string; text: string; badge: string }> = {
  system_one: { bg: 'bg-blue-50/40', border: 'border-blue-200/60', dot: 'bg-blue-500', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-600' },
  system_two: { bg: 'bg-blue-50/30', border: 'border-blue-200/50', dot: 'bg-blue-400', text: 'text-blue-600', badge: 'bg-blue-100/70 text-blue-600' },
  system_three: { bg: 'bg-blue-50/30', border: 'border-blue-200/50', dot: 'bg-blue-400', text: 'text-blue-600', badge: 'bg-blue-100/70 text-blue-500' },
};

export const AttachmentRailSelector: React.FC<AttachmentRailSelectorProps> = ({
  selectedAttachment,
  selectedRail,
  onAttachmentChange,
  onRailChange,
  disabled = false,
  isEnabled = true,
  selectedAttachment2,
  selectedRail2,
  onAttachment2Change,
  onRail2Change,
  showSecondAttachmentRail = false,
  onToggleSecondAttachmentRail,
  attachments,
  isLoading,
  error,
  panelRows1 = [],
  onPanelRows1Change,
  panelRows2 = [],
  onPanelRows2Change,
  totalModuleQuantity = 0,
  globalTotalAssigned = 0,
  globalRemainingPanels = 0,
  configuredSystems = [DEFAULT_SYSTEM_KEY],
  allSystemsModuleData,
  perSystemAssigned = {},
  auroraPanelRowsPool,
  structureNumber,
  structuresRoofsDetails,
  onAssignPoolRow,
  onUnassignPoolRow,
  onAssignPoolRow2,
  onUnassignPoolRow2,
}) => {
  const [availableRails, setAvailableRails] = useState<Rail[]>([]);
  const [availableRails2, setAvailableRails2] = useState<Rail[]>([]);

  // Effective systems: if none configured yet, default to system_one
  const effectiveSystems = configuredSystems.length > 0 ? configuredSystems : [DEFAULT_SYSTEM_KEY];

  // Case 1: Aurora pool mode — selectable list instead of manual rows
  // Pool is non-null (even if empty, meaning all rows are assigned) when Aurora workflow was completed
  const isAuroraPoolMode = auroraPanelRowsPool !== null && auroraPanelRowsPool !== undefined;

  // Filter rows by system_key for a given rail's panel rows
  const getSystemRows = (allRows: PanelRow[], systemKey: SystemKey): PanelRow[] =>
    allRows.filter(r => (r.system_key ?? DEFAULT_SYSTEM_KEY) === systemKey);

  // Compute per-system remaining for this system across ALL structures
  const getSystemRemaining = (systemKey: SystemKey): number => {
    const systemQty = allSystemsModuleData?.[systemKey]?.quantity || 0;
    const systemAssigned = perSystemAssigned[systemKey] || 0;
    return systemQty - systemAssigned;
  };

  // --- Generic handlers that work for both rail 1 and rail 2 ---
  const handleAddRow = (
    allRows: PanelRow[],
    onChange: ((rows: PanelRow[]) => void) | undefined,
    systemKey: SystemKey,
  ) => {
    if (!onChange) return;
    const systemRows = getSystemRows(allRows, systemKey);
    if (systemRows.length >= PANEL_ROWS_MAX) return;
    if (getSystemRemaining(systemKey) <= 0) return;
    const newRow: PanelRow = {
      id: generateRowId(),
      orientation: PanelOrientation.LANDSCAPE,
      panel_count: 0,
      system_key: systemKey,
    };
    onChange([...allRows, newRow]);
  };

  const handleRemoveRow = (
    allRows: PanelRow[],
    onChange: ((rows: PanelRow[]) => void) | undefined,
    rowId: string,
  ) => {
    if (!onChange) return;
    onChange(allRows.filter(row => row.id !== rowId));
  };

  const handleRowChange = (
    allRows: PanelRow[],
    onChange: ((rows: PanelRow[]) => void) | undefined,
    rowId: string,
    field: 'orientation' | 'panel_count',
    value: string | number,
    systemKey: SystemKey,
  ) => {
    if (!onChange) return;
    onChange(allRows.map(row => {
      if (row.id !== rowId) return row;
      if (field === 'orientation') {
        return { ...row, orientation: value as PanelOrientation };
      }
      const currentRowValue = row.panel_count || 0;
      const systemRemaining = getSystemRemaining(systemKey);
      const maxAllowed = systemRemaining + currentRowValue;
      const newValue = Math.max(0, Math.min(Number(value), maxAllowed));
      return { ...row, panel_count: newValue };
    }));
  };

  useEffect(() => {
    if (selectedAttachment) {
      const rails = selectedAttachment.rails || [];
      setAvailableRails(rails);
      if (selectedRail && !rails.some(rail => rail.name === selectedRail.name)) {
        onRailChange(undefined);
      }
    } else {
      setAvailableRails([]);
      onRailChange(undefined);
    }
  }, [selectedAttachment]);

  useEffect(() => {
    if (selectedAttachment2) {
      const rails = selectedAttachment2.rails || [];
      setAvailableRails2(rails);
      if (selectedRail2 && onRail2Change && !rails.some(rail => rail.name === selectedRail2.name)) {
        onRail2Change(undefined);
      }
    } else {
      setAvailableRails2([]);
      if (onRail2Change) {
        onRail2Change(undefined);
      }
    }
  }, [selectedAttachment2]);

  const handleAttachmentSelect = (option: any) => {
    if (option) {
      const attachment = attachments.find(a => a.id === option.id);
      onAttachmentChange(attachment);
    } else {
      onAttachmentChange(undefined);
    }
  };

  const handleRailSelect = (option: any) => {
    if (option) {
      const rail = availableRails.find(r => r.id === option.id);
      onRailChange(rail);
    } else {
      onRailChange(undefined);
    }
  };

  const handleAttachment2Select = (option: any) => {
    if (onAttachment2Change) {
      if (option) {
        const attachment = attachments.find(a => a.id === option.id);
        onAttachment2Change(attachment);
      } else {
        onAttachment2Change(undefined);
      }
    }
  };

  const handleRail2Select = (option: any) => {
    if (onRail2Change) {
      if (option) {
        const rail = availableRails2.find(r => r.id === option.id);
        onRail2Change(rail);
      } else {
        onRail2Change(undefined);
      }
    }
  };

  const attachmentOptions = attachments.map(attachment => ({
    id: attachment.id,
    label: attachment.name
  }));

  const railOptions = availableRails.map(rail => ({
    id: rail.id,
    label: `${rail.name} (${rail.domestic_status || (rail as any).domesticStatus})`
  }));

  const railOptions2 = availableRails2.map(rail => ({
    id: rail.id,
    label: `${rail.name} (${rail.domestic_status || (rail as any).domesticStatus})`
  }));

  const isRailDisabled = disabled || !selectedAttachment || availableRails.length === 0;
  const isRail2Disabled = disabled || !selectedAttachment2 || availableRails2.length === 0;

  const orientationOptions = PANEL_ORIENTATION_OPTIONS.map(opt => ({
    id: opt.value,
    label: opt.label
  }));

  /** Renders Aurora pool selectable list for a given rail (Case 1) */
  const renderAuroraPoolSection = (
    selectedRailForSection: Rail | undefined,
    assignedRows: PanelRow[],
    onAssign: ((rowId: string) => void) | undefined,
    onUnassign: ((rowId: string) => void) | undefined,
  ) => {
    if (!selectedRailForSection || !onAssign || !onUnassign || !auroraPanelRowsPool) return null;

    // Split pool: "Suggested" = rows whose structure_index matches this structure
    const suggestedRows = auroraPanelRowsPool.filter(r =>
      r.structure_index === structureNumber
    );
    const otherRows = auroraPanelRowsPool.filter(r =>
      r.structure_index !== structureNumber
    );

    return (
      <div className="mt-4 pt-3 border-t border-slate-200/70 space-y-3">
        {/* Assigned rows */}
        {assignedRows.length > 0 && (
          <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <h4 className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">
                Assigned to this Rail ({assignedRows.reduce((s, r) => s + (r.panel_count || 0), 0)} panels)
              </h4>
            </div>
            <div className="space-y-1">
              {assignedRows.map((row) => (
                  <div key={row.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-slate-200/80">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] 3xl:text-[11px] font-bold ${
                        row.orientation === PanelOrientation.LANDSCAPE ? 'bg-blue-100/60 text-blue-600' : 'bg-blue-100/50 text-blue-600'
                      }`}>
                        {row.orientation === PanelOrientation.LANDSCAPE ? 'Landscape' : 'Portrait'}
                      </span>
                      <span className="text-[10px] 3xl:text-xs font-bold text-slate-700">{row.panel_count} panels</span>
                    </div>
                    <button
                      onClick={() => onUnassign(row.id)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] 3xl:text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors flex-shrink-0"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Remove
                    </button>
                  </div>
              ))}
            </div>
          </div>
        )}

        {/* Available rows — Suggested */}
        {suggestedRows.length > 0 && (
          <div className="bg-slate-50/40 border border-slate-200/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <h4 className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Suggested</h4>
            </div>
            <div className="space-y-1">
              {suggestedRows.map((poolRow) => (
                  <div key={poolRow.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] 3xl:text-[11px] font-bold ${
                        poolRow.orientation === PanelOrientation.LANDSCAPE ? 'bg-blue-100/60 text-blue-600' : 'bg-blue-100/50 text-blue-600'
                      }`}>
                        {poolRow.orientation === PanelOrientation.LANDSCAPE ? 'Landscape' : 'Portrait'}
                      </span>
                      <span className="text-[10px] 3xl:text-xs font-bold text-slate-700">{poolRow.panel_count} panels</span>
                    </div>
                    <button
                      onClick={() => onAssign(poolRow.id)}
                      className="px-2 py-0.5 text-[9px] 3xl:text-[11px] font-semibold text-slate-600 bg-slate-50 border border-slate-300 rounded hover:bg-slate-100 transition-colors flex-shrink-0"
                    >
                      + Assign
                    </button>
                  </div>
              ))}
            </div>
          </div>
        )}

        {/* Available rows — Other */}
        {otherRows.length > 0 && (
          <div className="bg-slate-50/40 border border-slate-200/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-slate-400 rounded-full" />
              <h4 className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Other Available</h4>
            </div>
            <div className="space-y-1">
              {otherRows.map((poolRow) => {
                const otherStructName = structuresRoofsDetails?.[poolRow.structure_index - 1]?.structure_name ?? `Structure ${poolRow.structure_index}`;
                return (
                  <div key={poolRow.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] 3xl:text-[11px] font-bold ${
                        poolRow.orientation === PanelOrientation.LANDSCAPE ? 'bg-blue-100/60 text-blue-600' : 'bg-blue-100/50 text-blue-600'
                      }`}>
                        {poolRow.orientation === PanelOrientation.LANDSCAPE ? 'Landscape' : 'Portrait'}
                      </span>
                      <span className="text-[10px] 3xl:text-xs font-bold text-slate-700">{poolRow.panel_count} panels</span>
                      <span className="text-[9px] 3xl:text-[11px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {otherStructName}
                      </span>
                    </div>
                    <button
                      onClick={() => onAssign(poolRow.id)}
                      className="px-2 py-0.5 text-[9px] 3xl:text-[11px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded hover:bg-slate-100 transition-colors flex-shrink-0"
                    >
                      + Assign
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state when all pool rows are assigned */}
        {suggestedRows.length === 0 && otherRows.length === 0 && assignedRows.length === 0 && (
          <div className="text-center py-4 text-slate-400 text-xs 3xl:text-sm">
            All Aurora panel rows have been assigned to other rails.
          </div>
        )}
      </div>
    );
  };

  /** Renders per-system Panel Rows sections for a given rail (Case 2: Manual) */
  const renderPanelRowsSections = (
    selectedRailForSection: Rail | undefined,
    allRows: PanelRow[],
    onChange: ((rows: PanelRow[]) => void) | undefined,
  ) => {
    if (!selectedRailForSection || !onChange) return null;

    return (
      <div className="mt-4 pt-3 border-t border-slate-200/70 space-y-3">
        {effectiveSystems.map(systemKey => {
          const colors = SYSTEM_COLORS[systemKey];
          const modInfo = allSystemsModuleData?.[systemKey];
          const systemQty = modInfo?.quantity || 0;
          const systemAssigned = perSystemAssigned[systemKey] || 0;
          const systemRemaining = systemQty - systemAssigned;
          const systemRows = getSystemRows(allRows, systemKey);
          const canAddRow = systemRows.length < PANEL_ROWS_MAX && systemRemaining > 0;

          return (
            <div key={systemKey} className={`${colors.bg} border ${colors.border} rounded-xl p-3`}>
              {/* System header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 ${colors.dot} rounded-full`}></div>
                  <h4 className={`text-[11px] 3xl:text-xs uppercase tracking-[0.2em] font-semibold ${colors.text}`}>
                    Panel Rows — {SYSTEM_LABELS[systemKey]}
                  </h4>
                  {modInfo?.moduleName && (
                    <span className="text-[9px] 3xl:text-[11px] text-slate-500 bg-white/80 px-1.5 py-0.5 rounded truncate max-w-[160px]">
                      {modInfo.moduleName}
                    </span>
                  )}
                  {modInfo?.width && modInfo?.height && (
                    <span className="text-[9px] 3xl:text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                      {mmToInch(modInfo.width)}" × {mmToInch(modInfo.height)}"
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] 3xl:text-xs">
                  <span className={`font-medium ${colors.text}`}>
                    Qty: <span className="font-bold">{systemQty}</span>
                  </span>
                  <span className="text-slate-600 font-medium">
                    Assigned: <span className="font-bold">{systemAssigned}</span>
                  </span>
                  <span className={`font-medium ${
                    systemRemaining < 0 ? 'text-red-600' : systemRemaining === 0 ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    Remaining: <span className="font-bold">{systemRemaining}</span>
                  </span>
                </div>
              </div>

              {/* Rows */}
              <div className="max-h-64 overflow-y-auto mb-3">
                {systemRows.length === 0 ? (
                  <div className="text-center py-4 text-slate-400 text-xs 3xl:text-sm">
                    {systemRemaining > 0 ? 'No rows configured. Click "Add Row" to start.' : 'All modules assigned.'}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {systemRows.map((row, index) => {
                      const maxForThisRow = systemRemaining + (row.panel_count || 0);
                      return (
                        <div
                          key={row.id}
                          className="flex items-center gap-1.5 p-1.5 bg-white rounded-lg border border-slate-200 hover:border-blue-200 transition-colors"
                        >
                          <span className="text-[9px] 3xl:text-[11px] font-semibold text-slate-500 w-4">#{index + 1}</span>
                          <div className="flex-1 min-w-0">
                            <SearchDropdown
                              options={orientationOptions}
                              onSelect={(option) => {
                                if (option?.id) {
                                  handleRowChange(allRows, onChange, row.id, 'orientation', option.id, systemKey);
                                }
                              }}
                              placeholder="Orientation"
                              className="text-xs 3xl:text-sm"
                              inputClassName="h-6 text-[10px] 3xl:text-xs"
                              value={{ id: row.orientation, label: PANEL_ORIENTATION_OPTIONS.find(o => o.value === row.orientation)?.label || 'Landscape' }}
                            />
                          </div>
                          <div className="w-12">
                            <input
                              type="number"
                              min="0"
                              max={maxForThisRow}
                              value={row.panel_count || ''}
                              onChange={(e) => handleRowChange(allRows, onChange, row.id, 'panel_count', e.target.value, systemKey)}
                              placeholder="Qty"
                              className="w-full h-6 px-1.5 text-[10px] 3xl:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveRow(allRows, onChange, row.id)}
                            className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Remove row"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add Row */}
              <div className="flex justify-center">
                <button
                  onClick={() => handleAddRow(allRows, onChange, systemKey)}
                  disabled={!canAddRow}
                  className="py-1 px-2.5 bg-blue-500 hover:bg-blue-600 text-white text-[10px] 3xl:text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Row ({systemRows.length}/{PANEL_ROWS_MAX})
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isEnabled) {
    return (
      <div className="border border-emerald-200/60 rounded-lg bg-emerald-50/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 7h12M6 12h12M6 17h8" />
            </svg>
            <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Attachments & Rails</span>
          </div>
          <div className="text-center py-6">
            <p className="text-gray-400 text-xs 3xl:text-sm">Complete Job Selection to unlock configuration</p>
          </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200/70">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 7h12M6 12h12M6 17h8" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Attachments & Rails</span>
          <span className="text-[9px] 3xl:text-[11px] text-slate-400 font-medium">Up to 2 per structure</span>
        </div>
        {!showSecondAttachmentRail && onToggleSecondAttachmentRail && (
          <button
            onClick={onToggleSecondAttachmentRail}
            disabled={disabled}
            className="px-2.5 py-1 font-semibold bg-emerald-600 text-white rounded-full hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            style={{ fontSize: '0.6875rem' }}
          >
            + Add Second
          </button>
        )}
      </div>

      <div className="space-y-4">
      {/* Attachment & Rail 1 */}
      <div className="border border-emerald-200/60 rounded-lg bg-emerald-50/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 7h12M6 12h12M6 17h8" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Attachment & Rail 1</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
              Attachment *
            </label>
            <SearchDropdown
              options={attachmentOptions}
              onSelect={handleAttachmentSelect}
              placeholder="Choose attachment..."
              isLoading={isLoading}
              error={error}
              disabled={disabled}
              className="w-full"
              value={selectedAttachment ? { id: selectedAttachment.id, label: selectedAttachment.name } : null}
            />
          </div>

          <div>
            <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
              Rail *
            </label>
            <SearchDropdown
              options={railOptions}
              onSelect={handleRailSelect}
              placeholder={selectedAttachment ? "Choose rail..." : "Select attachment first"}
              disabled={isRailDisabled}
              className="w-full"
              value={selectedRail ? { id: selectedRail.id, label: `${selectedRail.name} (${selectedRail.domestic_status || (selectedRail as any).domesticStatus})` } : null}
            />
            {selectedAttachment && availableRails.length === 0 && (
              <p className="text-xs 3xl:text-sm text-slate-500 mt-1">No compatible rails available for selected attachment</p>
            )}
          </div>
        </div>

        {/* Panel Rows Configuration for Rail 1 */}
        {isAuroraPoolMode
          ? renderAuroraPoolSection(selectedRail, panelRows1, onAssignPoolRow, onUnassignPoolRow)
          : renderPanelRowsSections(selectedRail, panelRows1, onPanelRows1Change)
        }
      </div>

      {/* Attachment & Rail 2 */}
      {showSecondAttachmentRail && (
        <div className="border border-emerald-200/60 rounded-lg bg-emerald-50/40 p-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 7h12M6 12h12M6 17h8" />
              </svg>
              <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Attachment & Rail 2</span>
            </div>
            {onToggleSecondAttachmentRail && (
              <button
                onClick={onToggleSecondAttachmentRail}
                className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-slate-100 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                title="Remove"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
                Attachment *
              </label>
              <SearchDropdown
                options={attachmentOptions}
                onSelect={handleAttachment2Select}
                placeholder="Choose attachment..."
                disabled={disabled}
                className="w-full"
                value={selectedAttachment2 ? { id: selectedAttachment2.id, label: selectedAttachment2.name } : null}
              />
            </div>

            <div>
              <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
                Rail *
              </label>
              <SearchDropdown
                options={railOptions2}
                onSelect={handleRail2Select}
                placeholder={selectedAttachment2 ? "Choose rail..." : "Select attachment first"}
                disabled={isRail2Disabled}
                className="w-full"
                value={selectedRail2 ? { id: selectedRail2.id, label: `${selectedRail2.name} (${selectedRail2.domestic_status || (selectedRail2 as any).domesticStatus})` } : null}
              />
              {selectedAttachment2 && availableRails2.length === 0 && (
                <p className="text-xs 3xl:text-sm text-slate-500 mt-1">No compatible rails available for selected attachment</p>
              )}
            </div>
          </div>

          {/* Panel Rows Configuration for Rail 2 */}
          {isAuroraPoolMode
            ? renderAuroraPoolSection(selectedRail2, panelRows2, onAssignPoolRow2, onUnassignPoolRow2)
            : renderPanelRowsSections(selectedRail2, panelRows2, onPanelRows2Change)
          }
        </div>
      )}
      </div>
    </div>
  );
};
